import { Injectable, Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, startWith } from 'rxjs';

import { dataSelectionVocabulary } from '@core/filters/path-filter/vocabulary/data-selection.vocabulary';
import { FilterVocabulary } from '@core/filters/path-filter/vocabulary/vocabulary';
import { JsonSchema } from '@core/models/transformers.model';
import {
  selectAvailableEntities,
  selectDataspaceError,
  selectDataspaceModel,
  selectTransformersInfo
} from '@core/store/dataspace/dataspace.selectors';
import { extractDataspaceIdFromUrl } from '@core/utils/dataspace-url.utils';

// Parsed as the route sync parses it before fetching, so `/ds/07` matches the dataspace 7 it loads.
function parseDataspaceId(segment: string | null): number | null {
  const id = segment === null ? NaN : parseInt(segment, 10);
  return Number.isNaN(id) ? null : id;
}

@Injectable({ providedIn: 'root' })
export class PathFilterVocabularyService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly storedEntities = this.store.selectSignal(selectAvailableEntities);
  private readonly storedTransformersInfo = this.store.selectSignal(selectTransformersInfo);

  private readonly dataspace = this.store.selectSignal(selectDataspaceModel);
  private readonly loadError = this.store.selectSignal(selectDataspaceError);
  private readonly dataspaceIdOnScreen = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(null),
      map(() => parseDataspaceId(extractDataspaceIdFromUrl(this.router.url)))
    ),
    { initialValue: null }
  );

  // The store keeps the catalogue of the dataspace left behind while the next one loads, and
  // after that load failed; read against it, a filter would be decoded with the wrong fields.
  private readonly catalogueOnScreen = computed(() => {
    const onScreen = this.dataspaceIdOnScreen();
    return onScreen === null || this.dataspace()?.id === onScreen;
  });
  private readonly entities = computed(() =>
    this.catalogueOnScreen() ? this.storedEntities() : []
  );
  private readonly transformersInfo = computed(() =>
    this.catalogueOnScreen() ? this.storedTransformersInfo() : []
  );

  // A load that failed, or that settled with nothing to describe, never fills the catalogue, so
  // it must also release callers waiting on it; they then see a null vocabulary. Both only
  // count for the dataspace being viewed.
  readonly catalogueLoaded: Signal<boolean> = computed(() => {
    if (this.entities().length > 0 && this.transformersInfo().length > 0) return true;
    const onScreen = this.dataspaceIdOnScreen();
    if (onScreen === null) return false;
    return this.loadError()?.dataspaceId === onScreen || this.dataspace()?.id === onScreen;
  });

  // Building a vocabulary resolves the schema of every field path, and one table asks for it from
  // both its filter bar and its presets menu. A new catalogue starts a new map.
  private readonly built = computed(() => {
    this.entities();
    this.transformersInfo();
    return new Map<string, FilterVocabulary | null>();
  });

  vocabularyFor(entityType: Signal<string>): Signal<FilterVocabulary | null> {
    return computed(() => this.vocabularyOf(entityType()));
  }

  /** Tracks the catalogue when read inside a `computed` or an effect. */
  vocabularyOf(entityType: string): FilterVocabulary | null {
    const built = this.built();
    const type = entityType.toUpperCase();
    if (!built.has(type)) built.set(type, this.build(type));
    return built.get(type) ?? null;
  }

  private build(type: string): FilterVocabulary | null {
    const schema = this.schemaOf(type);
    if (!schema) return null;
    return dataSelectionVocabulary(type, schema, (target) => this.schemaOf(target));
  }

  private schemaOf(entityType: string): JsonSchema | undefined {
    const wanted = entityType.toLowerCase();
    const versionName = this.entities().find(
      (entity) => entity.name?.toLowerCase() === wanted
    )?.versions[0]?.versionName;
    if (!versionName) return undefined;
    for (const info of this.transformersInfo()) {
      for (const model of info.dataModels) {
        if (model.metadata.entityTypeVersion === versionName) return model.metadata.schema;
      }
    }
    return undefined;
  }
}
